import { useCallback, useEffect, useState } from 'react';

import type {
  CreatePublishTaskMutation,
  CreatePublishTaskMutationVariables,
} from '../../activity-builder/settings/publish/settings-publish.graphql.generated';
import {
  CREATE_PUBLISH_TASK,
  GET_PUBLISH_TASK_BY_ID,
} from 'approot/activity-builder/settings/publish/settings-publish.graphql';
import { useLoggingMutation, useLoggingQuery } from 'lib/apollo/apollo-hooks';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import type {
  GetPublishTaskByIdQuery,
  GetPublishTaskByIdQueryVariables,
} from '../../activity-builder/settings/publish/settings-publish.graphql.generated';
import {
  ActivitiesContentType,
  LanguageCodes,
  PUBLISH_TASK_STATUS_PDF_GENERATED,
} from 'global/constants';
import { PStyled } from 'approot/shared/styles/typography.style';
import Bouncer from 'approot/shared/bouncer/bouncer';
import { Box } from 'approot/shared/design-system/box/box.style';
import SettingsDownloadPrintContent from 'approot/activity-builder/settings/publish/settings-download-print-content';
import { studentAuthApolloContextOption } from 'approot/student-class-to-do/student-class-to-do.utils';

type UploadAssetKey = {
  key: string;
  url: string;
  language: LanguageCodes;
};

type PrintAssetKey = {
  key: string;
  language: LanguageCodes;
  publicToken: string | null;
};

type Props = {
  contentId: number;
  contentType: ActivitiesContentType;
  classId: number | null;
  language: LanguageCodes;
  version: string;
  isStudentDownload: boolean;
  isPublic?: boolean;
  hideDefaultMessage?: boolean;
  onComplete: (assetKeys: PrintAssetKey[] | undefined) => void;
};

const DownloadActivitiesLanguageOnDemand = ({
  contentId,
  contentType,
  classId,
  language,
  version,
  isStudentDownload = false,
  isPublic = false,
  hideDefaultMessage = false,
  onComplete,
}: Props) => {
  const [pollInterval, setPollInterval] = useState<number>(0);
  const [uploadAsset, setUploadAsset] = useState<
    UploadAssetKey[] | undefined
  >();

  const { showError } = useSnackbar();

  const handlePublishEnd = useCallback(
    (printAssetKey: PrintAssetKey[] | undefined) => {
      setPollInterval(0);
      setUploadAsset(undefined);
      onComplete(printAssetKey);
    },
    [onComplete]
  );

  const [createPublishTask, { data: createPublishTaskData }] =
    useLoggingMutation<
      CreatePublishTaskMutation,
      CreatePublishTaskMutationVariables
    >(
      CREATE_PUBLISH_TASK,
      {
        ...(isStudentDownload
          ? { context: studentAuthApolloContextOption() }
          : {}),
        fetchPolicy: 'network-only',
        onError: () => {
          handlePublishEnd(undefined);
          showError(
            'Oops! Something went wrong downloading the activities. Please try again.'
          );
        },
      },
      !isStudentDownload
    );

  const publishTaskId = createPublishTaskData?.createPublishTask.id;

  useLoggingQuery<GetPublishTaskByIdQuery, GetPublishTaskByIdQueryVariables>(
    GET_PUBLISH_TASK_BY_ID,
    {
      skip: !publishTaskId || pollInterval === 0,
      pollInterval: pollInterval,
      fetchPolicy: 'network-only',
      ...(isStudentDownload
        ? { context: studentAuthApolloContextOption() }
        : {}),

      variables: {
        id: publishTaskId!,
      },
      onCompleted: res => {
        if (
          res.getPublishTaskById.status === PUBLISH_TASK_STATUS_PDF_GENERATED &&
          res.getPublishTaskById.assetKeys?.print
        ) {
          handlePublishEnd(res.getPublishTaskById.assetKeys.print);
        }
      },
      onError: () => {
        handlePublishEnd(undefined);
        showError(
          'Oops! Something went wrong downloading the activities. Please try again.'
        );
      },
    },
    isPublic || isStudentDownload ? false : true
  );

  const handleRenderComplete = useCallback(
    async (contentString: string) => {
      if (uploadAsset) {
        await fetch(uploadAsset[0].url, {
          method: 'PUT',
          body: contentString,
        });
        setPollInterval(3000);
      }
    },
    [uploadAsset]
  );

  useEffect(() => {
    createPublishTask({
      variables: {
        input: {
          contentId,
          contentType,
          createNewVersion: false,
          languages: [language],
          version,
        },
      },
    }).then(res =>
      setUploadAsset(res.data?.createPublishTask.uploadUrls || undefined)
    );
  }, [contentId, contentType, language, createPublishTask, version]);

  return (
    <div>
      {!hideDefaultMessage && (
        <>
          <Box $marginBottom={24}>
            <Bouncer />
          </Box>
          <PStyled textAlignCenter marginBottom={4}>
            Your activities are downloading...
          </PStyled>
        </>
      )}

      {uploadAsset && (
        <SettingsDownloadPrintContent
          contentId={contentId}
          contentType={contentType}
          classId={classId}
          version={version}
          language={language}
          onRenderComplete={handleRenderComplete}
        />
      )}
    </div>
  );
};

export default DownloadActivitiesLanguageOnDemand;
